import { useToast } from '@chakra-ui/react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import type { Item } from '@revelio/iso-utility';

import {
  CompanySearchTree,
  type CompanySearchTreeHandle,
} from '../../../../../../shared/components/company-search-tree/company-search-tree';
import { useCompanyPlaceholderTree } from '../../../../../../shared/selection-lists/hooks/useCompanyPlaceholderTree';
import type { CompanyResultItem } from '../../../../../../shared/types/company-result-item';

export type ReportEntityCompanyEditorHandle = {
  handleClearSelections: () => void;
};

export interface ReportEntityCompanyEditorProps {
  limit: number;
  treeHeight: number;
  onSelectionsValue: (items: Record<string, CompanyResultItem>) => void;
  filterFn?: (item: Item) => boolean;
  noFilteredResultsMessage?: string;
}

const sortByEmployeeCount = (a: Item, b: Item) =>
  Number(b.raw_emp_count ?? 0) - Number(a.raw_emp_count ?? 0);

export const ReportEntityCompanyEditor = forwardRef<
  ReportEntityCompanyEditorHandle,
  ReportEntityCompanyEditorProps
>(
  (
    {
      limit,
      treeHeight,
      onSelectionsValue,
      filterFn,
      noFilteredResultsMessage,
    }: ReportEntityCompanyEditorProps,
    ref
  ) => {
    const [selections, setSelections] = useState<
      Record<string, CompanyResultItem>
    >({});

    const treeRef = useRef<CompanySearchTreeHandle>(null);
    const toast = useToast();

    const placeholderListNodes = useCompanyPlaceholderTree({
      companyFilterFn: filterFn,
    });

    useImperativeHandle(ref, () => ({
      handleClearSelections: () => {
        treeRef.current?.handleClearSelections();
      },
    }));

    const onSelectionsValueRef = useRef(onSelectionsValue);
    onSelectionsValueRef.current = onSelectionsValue;

    useEffect(() => {
      onSelectionsValueRef.current?.(selections);
    }, [selections]);

    const limitedSetSelections: React.Dispatch<
      React.SetStateAction<Record<string, CompanyResultItem>>
    > = useCallback(
      (action) => {
        setSelections((prev) => {
          const next = typeof action === 'function' ? action(prev) : action;
          if (Object.keys(next).length > limit) {
            toast({
              title: `Selection limit reached`,
              description: `You can select up to ${limit} companies.`,
              status: 'warning',
              duration: 3000,
              isClosable: true,
              position: 'top-right',
            });
            return prev;
          }
          return next;
        });
      },
      [limit, toast]
    );

    return (
      <CompanySearchTree
        ref={treeRef}
        placeholderListNodes={placeholderListNodes}
        selections={selections}
        setSelections={limitedSetSelections}
        sortFn={sortByEmployeeCount}
        singleSelect={limit === 1}
        transformResults={
          filterFn ? (results: Item[]) => results.filter(filterFn) : undefined
        }
        autoFocus
        treeHeight={treeHeight}
        noFilteredResultsMessage={noFilteredResultsMessage}
      />
    );
  }
);
